import React, { useEffect, useState } from "react";
import {
  Col,
  Form,
  Table,
  Row,
  Button,
  Card,
  Alert,
} from "@themesberg/react-bootstrap";
import { postApi } from "../../../redux/api";
import toast from "react-hot-toast";
import { confirmDialog } from "../../../components/ConfirmDialog";
import { accountTypes } from "../../accounts/AccountForm";
import useBankList from "../../../hooks/useBankList";
import { Typeahead } from "react-bootstrap-typeahead";

const TaxSetup = () => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [taxesList, setTaxesList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    tax_name: "",
    rate: "",
    tax_type: "State",
    rate_type: "fixed",
    bank_name: "",
    account_name: "",
    account_no: "",
    account_type: "",
  });
  const { bankList } = useBankList();

  const validateForm = () => {
    const newErrors = {};

    if (!form.tax_name.trim()) {
      newErrors.tax_name = "Tax name is required";
    }

    if (!form.rate || parseFloat(form.rate) < 0) {
      newErrors.rate = "Valid rate is required";
    }

    if (form.rate_type === "percentage" && parseFloat(form.rate) > 100) {
      newErrors.rate = "Percentage cannot exceed 100%";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getTaxes = () => {
    setLoading(true);
    postApi(`post_taxes`, {
      query_type: "select",
    })
      .then((data) => {
        console.log("Tax data response:", data);
        if (data.success && data.result) {
          // Handle different response structures
          const taxes = Array.isArray(data.result) ? data.result :
            Array.isArray(data.result[0]) ? data.result[0] : [];
          setTaxesList(taxes);
        } else {
          setTaxesList([]);
        }
      })
      .catch((error) => {
        console.error("Error fetching tax data:", error.message);
        toast.error("An error occurred while fetching data.");
        setTaxesList([]);
      })
      .finally(() => {
        setLoading(false);
      });
  };


  useEffect(() => {
    getTaxes()
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      let formData = { ...form };
      if (form.id) {
        formData.query_type = 'update';
      } else {
        formData.query_type = 'insert';
      }

      const response = await postApi(`post_taxes`, formData);

      if (response && response.success) {
        toast.success(editingIndex !== null ? "Tax updated successfully" : "Tax added successfully");
        getTaxes();
        resetForm();
      } else {
        toast.error('An error occurred, please try again later.');
      }
    } catch (error) {
      console.error("Error submitting form data:", error.message);
      toast.error("An error occurred, please try again later.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm({
      tax_name: "",
      rate: "",
      tax_type: "State",
      rate_type: "fixed",
      bank_name: "",
      account_name: "",
      account_no: "",
      account_type: ""
    });
    setEditingIndex(null);
    setErrors({});
  };

  const handleChange = ({ target: { name, value } }) => {
    setForm(p => ({ ...p, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(p => ({ ...p, [name]: "" }));
    }
  };

  const handleEdit = (index) => {
    const item = taxesList[index];
    setForm(item);
    setEditingIndex(index);
    setErrors({});
  };

  const handleDelete = async (index) => {
    const item = taxesList[index];
    if (await confirmDialog(`Are you sure you want to delete "${item.tax_name}"?`, { title: "Delete tax", confirmText: "Delete" })) {
      postApi('post_taxes', { query_type: 'delete', id: item.id })
        .then(resp => {
          if (resp && resp.success) {
            toast.success("Successfully deleted");
            getTaxes();
          } else {
            toast.error("An error occurred, please try again later.");
          }
        }).catch(err => {
          console.log(err);
          toast.error("An error occurred, please try again later.");
        });
    }
  };

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4>Tax Setup</h4>
          {editingIndex !== null && (
            <Button variant="outline-secondary" size="sm" onClick={resetForm}>
              Cancel Edit
            </Button>
          )}
        </div>

        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={4}>
              <Form.Group controlId="taxName">
                <Form.Label>Tax Name <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  type="text"
                  value={form.tax_name}
                  name="tax_name"
                  size="sm"
                  onChange={handleChange}
                  isInvalid={!!errors.tax_name}
                  placeholder="Enter tax name"
                />
                <Form.Control.Feedback type="invalid">
                  {errors.tax_name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="amount">
                <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Check
                      type="radio"
                      label="Fixed"
                      name="amountType"
                      checked={form.rate_type === "fixed"}
                      onChange={() => setForm(p => ({ ...p, rate_type: "fixed" }))}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Check
                      type="radio"
                      label="Percentage"
                      name="amountType"
                      checked={form.rate_type === "percentage"}
                      onChange={() => setForm(p => ({ ...p, rate_type: "percentage" }))}
                    />
                  </Col>
                </Row>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.rate}
                  name="rate"
                  size="sm"
                  onChange={handleChange}
                  isInvalid={!!errors.rate}
                  placeholder={form.rate_type === "percentage" ? "Enter percentage" : "Enter amount"}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.rate}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group controlId="taxType">
                <Form.Label>Tax Type</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={handleChange}
                  name="tax_type"
                  value={form.tax_type}
                >
                  <option value="State">State</option>
                  <option value="Federal">Federal</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <Row className="my-3">
            <Col md={3}>
              <Form.Group controlId="bankName">
                <Form.Label>Bank Name</Form.Label>
                <Typeahead
                  id="bank_name"
                  labelKey="title"
                  options={bankList}
                  placeholder="--Select Bank--"
                  selected={bankList.filter(
                    (item) => item.title === form.bank_name
                  )}
                  onChange={(selected) => {
                    const value = selected.length > 0 ? selected[0].title : "";
                    setForm((p) => ({ ...p, bank_name: value }));
                    if (errors.bank_name) {
                      setErrors((p) => ({ ...p, bank_name: "" }));
                    }
                  }}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="account_name">
                <Form.Label>Account Name</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={form.account_name}
                  name="account_name"
                  onChange={handleChange}
                  placeholder="Enter account name"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="account_no">
                <Form.Label>Account Number</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={form.account_no}
                  name="account_no"
                  onChange={handleChange}
                  placeholder="Enter account number"
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group controlId="account_type">
                <Form.Label>Account Type</Form.Label>
                <Form.Select
                  size="sm"
                  onChange={handleChange}
                  value={form.account_type}
                  name="account_type"
                >
                  <option value="">--Select Type--</option>
                  {accountTypes.map((item, id) => (
                    <option key={id} value={item.title}>
                      {item.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          <div className="text-center">
            <Button
              variant="primary"
              type="submit"
              disabled={submitting}
              className="px-4"
            >
              {submitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  {editingIndex !== null ? "Updating..." : "Adding..."}
                </>
              ) : (
                editingIndex !== null ? "Update Tax" : "Add Tax"
              )}
            </Button>
          </div>
        </Form>
      </Card.Body>
      <Card.Body>
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4>Tax Data</h4>
              <small className="text-muted">{Array.isArray(taxesList) ? taxesList.length : 0} tax(es) configured</small>
            </div>

            {!Array.isArray(taxesList) || taxesList.length === 0 ? (
              <Alert variant="info">
                No taxes configured yet. Add your first tax using the form above.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table striped bordered hover>
                  <thead className="table-dark">
                    <tr>
                      <th>S/N</th>
                      <th>Tax Name</th>
                      <th>Rate</th>
                      <th>Type</th>
                      <th>Account Name</th>
                      <th>Account Number</th>
                      <th>Bank Name</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(taxesList) && taxesList.map((item, index) => (
                      <tr key={item.id || index}>
                        <td>{index + 1}</td>
                        <td>
                          <strong>{item.tax_name}</strong>
                          {item.tax_type && (
                            <small className="d-block text-muted">({item.tax_type})</small>
                          )}
                        </td>
                        <td>
                          <span className={`badge ${item.rate_type === 'percentage' ? 'bg-success' : 'bg-primary'}`}>
                            {item.rate_type === 'percentage' ? `${item.rate}%` : `₦${parseFloat(item.rate || 0).toLocaleString()}`}
                          </span>
                        </td>
                        <td>{item.tax_type || 'N/A'}</td>
                        <td>{item.account_name || 'N/A'}</td>
                        <td>{item.account_no || 'N/A'}</td>
                        <td>{item.bank_name || 'N/A'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              size="sm"
                              variant="outline-primary"
                              onClick={() => handleEdit(index)}
                              title="Edit tax"
                            >
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() => handleDelete(index)}
                              title="Delete tax"
                            >
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
};

export default TaxSetup;
